import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createModeratedWord, deleteModeratedWord, editModeratedWord, getForbiddenAndModeratedWords } from '~/api/mangeWords';
import { WordCategory } from '~/containers/ManageWords/components/ManageWordsList';
import CobuConstants from '~/helpers/cobuConstants';
import {  ModeratedWord } from '~/types/manageWords';

type LoadingConfig = {
  flag: boolean;
  comment?: string;
};

const defaultLoadingConfig: LoadingConfig = {
  flag: false,
  comment: ''
};

export default function useManageWords() {
  const [forbiddenWords, setForbiddenWords] = useState<ModeratedWord[]>([]);
  const [moderatedWords, setModeratedWords] = useState<ModeratedWord[]>([]);
  const [loadingConfig, setLoadingConfig] = useState<LoadingConfig>(defaultLoadingConfig);

  useEffect(() => {
    fetchForbiddenAndModeratedWords();
  }, []);

  const fetchForbiddenAndModeratedWords = async (refresh: boolean = false) => {
    setLoadingConfig({
      flag: true,
      comment: refresh
        ? CobuConstants.manageWords.refreshing
        : CobuConstants.manageWords.fetching
    });
    try {
      const {data} = await getForbiddenAndModeratedWords();
      setForbiddenWords(data.forbiddenWords);
      setModeratedWords(data.moderatedWords);
      setLoadingConfig({ flag: false });
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const createWord = async (term: string, type: WordCategory) => {
    setLoadingConfig({
      flag: true,
      comment: type === 'forbidden' ? CobuConstants.manageWords.creatingForbiddenWord : CobuConstants.manageWords.creatingModeratedWord
    });
    try {
      await createModeratedWord(term, type);
      fetchForbiddenAndModeratedWords(true);
      toast(type === 'forbidden' ? CobuConstants.manageWords.createForbiddenWordSuccess : CobuConstants.manageWords.createModeratedWordSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const updateWord = async (term: string, type: WordCategory, uuid: string) => {
    setLoadingConfig({
      flag: true,
      comment: type === 'forbidden' ? CobuConstants.manageWords.editingForbiddenWord : CobuConstants.manageWords.editingModeratedWord
    });
    try {
      await editModeratedWord(term, type, uuid);

      fetchForbiddenAndModeratedWords(true);
      toast(type === 'forbidden' ? CobuConstants.manageWords.editForbiddenWordSuccess : CobuConstants.manageWords.editModeratedWordSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  const deleteWord = async (type: WordCategory, uuid: string) => {
    setLoadingConfig({
      flag: true,
      comment: type === 'forbidden' ? CobuConstants.manageWords.deletingForbiddenWord : CobuConstants.manageWords.deletingModeratedWord
    });
    try {
      await deleteModeratedWord(uuid, type);

      fetchForbiddenAndModeratedWords(true);
      toast(type === 'forbidden' ? CobuConstants.manageWords.deleteForbiddenWordSuccess : CobuConstants.manageWords.deleteModeratedWordSuccess);
    } catch (error) {
      setLoadingConfig({ flag: false });
      if (error) {
        toast(error?.message);
      }
    }
  };

  return {
    forbiddenWords,
    moderatedWords,
    loadingConfig,
    createWord,
    updateWord,
    deleteWord,
  };
};
